import {
  ECurrency,
  ELanguage,
  ESalutation,
  EVoucherType,
} from '@ringana/configs';
import { differenceInDays, format, parse } from 'date-fns';
import {
  IApiCoupon,
  IApiCustomerDataSheet,
  IApiCustomerDataSheetAddress,
  IApiCustomerDataSheetBaseData,
  IApiCustomerDataSheetCompanyData,
  IApiCustomerDataSheetContact,
  IApiCustomerDatasheetFavoriteProduct,
  IApiCustomerDataSheetInvoice,
  IApiDataSheetOrder,
  IApiDatasheetOrdersFilter,
} from '../common/interfaces/api.interface';
import {
  ICustomerDataSheet,
  ICustomerDataSheetAddress,
  ICustomerDataSheetBaseData,
  ICustomerDataSheetCompanyData,
  ICustomerDataSheetContact,
  ICustomerDatasheetFavoriteProduct,
  ICustomerDataSheetInvoice,
  IDataSheetOrder,
  IDatasheetOrdersFilter,
} from '../common/interfaces/customers.interface';
import { ICoupon, IShoppingCartItem, ICustomerCoupon } from '../common/interfaces/cart.interface';
import {
  IApiAssignableVoucherInfo,
  IAssignableVoucherInfo,
} from '../common/interfaces/vouchers.interface';
import { IPinterestProductData, ITrackingProductData } from '@/common/interfaces/tracking.interface';

export default class TransformHelper {
  public static transformApiCustomerDataSheetToCustomerDataSheet(apiDataSheet: IApiCustomerDataSheet): ICustomerDataSheet {
    return {
      addresses: TransformHelper.transformApiCustomerDataSheetAddressesToCustomerDataSheetAddresses(apiDataSheet.addresses ?? {}),
      baseData: TransformHelper.transformApiCustomerDataSheetBaseDataToCustomerDataSheetBaseData(apiDataSheet.basedata),
      companyData: TransformHelper.transformApiCustomerDataSheetCompanyDataToCustomerDataSheetCompanyData(apiDataSheet.companydata),
      contacts: TransformHelper.transformApiCustomerDataSheetContactsToCustomerDataSheetContacts(apiDataSheet.contacts ?? {}),
      coupons: TransformHelper.transformApiCouponsToCoupons(apiDataSheet.coupons ?? {}),
      created: apiDataSheet.created,
      customerName: apiDataSheet.customerName,
      customerNumber: +apiDataSheet.customerNumber,
      favorites: TransformHelper.transformApiCustomerDataSheetFavoritesToCustomerDataSheetFavorites(apiDataSheet.favorites ?? []),
      invoices: TransformHelper.transformApiCustomerDataSheetInvoicesToCustomerDataSheetInvoices(apiDataSheet.invoices ?? {}),
      partner: apiDataSheet.partner,
      partnerNumber: apiDataSheet.partnerNumber,
      partnerNumberRecommender: apiDataSheet.partnerNumberRecommender,
      partnerSince: apiDataSheet.partnerSince,
    };
  }

  public static transformApiCustomerDataSheetBaseDataToCustomerDataSheetBaseData(apiBaseData: IApiCustomerDataSheetBaseData): ICustomerDataSheetBaseData {
    return {
      acquisition: apiBaseData.acquisition ?? ``,
      acquisitionAnnotation: apiBaseData.acquisitionAnnotation,
      birthdate: apiBaseData.birthdate ?? ``,
      currency: apiBaseData.currency ?? ECurrency.EUR,
      email: apiBaseData.email ?? ``,
      firstname: apiBaseData.firstname ?? ``,
      firstnamePartner: apiBaseData.firstnamePartner ?? ``,
      formalSalutation: apiBaseData.formalSalutation ?? ``,
      language: apiBaseData.language ?? ELanguage.DE,
      lastname: apiBaseData.lastname ?? ``,
      lastnamePartner: apiBaseData.lastnamePartner ?? ``,
      newsletter: apiBaseData.newsletter ?? false,
      newsletterUnsubscribed: apiBaseData.newsletterUnsubscribed ?? false,
      salutation: apiBaseData.salutation ?? ESalutation.MR,
      taxnumber: apiBaseData.taxnumber ?? ``,
    };
  }

  public static transformApiCustomerDataSheetCompanyDataToCustomerDataSheetCompanyData(apiCompanyData: IApiCustomerDataSheetCompanyData): ICustomerDataSheetCompanyData {
    return {
      company: apiCompanyData.company ?? ``,
      companyAdditionalText: apiCompanyData.companyAdditionalText ?? ``,
      companyBookingNumber: apiCompanyData.companyBookingNumber ?? ``,
      uid: apiCompanyData.uid ?? ``,
    };
  }

  public static transformApiCustomerDataSheetFavoritesToCustomerDataSheetFavorites(apiFavorites: IApiCustomerDatasheetFavoriteProduct[]): ICustomerDatasheetFavoriteProduct[] {
    return apiFavorites.map(apiFavorite => TransformHelper.transformApiCustomerDataSheetFavoriteToCustomerDataSheetFavorite(apiFavorite));
  }

  public static transformApiCustomerDataSheetFavoriteToCustomerDataSheetFavorite(apiFavorite: IApiCustomerDatasheetFavoriteProduct): ICustomerDatasheetFavoriteProduct {
    return {
      amount: apiFavorite.amount ?? 0,
      availableVariants: apiFavorite.availableVariants.map(variant => ({
        ...variant,
        unit: `products-ufe:productInfos.units.unit${variant.unitId}`,
      })),
      basePrice: {
        ...apiFavorite.basePrice,
        unit: `products-ufe:productInfos.units.unit${apiFavorite.basePrice.unitId}`,
      },
      contentsAmount: apiFavorite.contentsAmount ?? ``,
      currency: apiFavorite.currency ?? ECurrency.EUR,
      deliveryStatus: apiFavorite.deliveryStatus,
      deliveryStatusId: apiFavorite.deliveryStatusId,
      deliveryStatusId2: apiFavorite.deliveryStatusId2,
      discountDate: apiFavorite.discountDate,
      discountPercentage: apiFavorite.discountPercentage,
      image: apiFavorite.image ?? ``,
      isMerchandise: apiFavorite.isMerchandise ?? false,
      lang: apiFavorite.lang ?? ELanguage.DE,
      link: apiFavorite.link ?? ``,
      matchCode: apiFavorite.matchCode ?? ``,
      matchcode: apiFavorite.matchcode ?? ``,
      maxPerCart: apiFavorite.maxPerCart,
      nemBackgroundColor: apiFavorite.nemBackgroundColor ?? false,
      notSearchable: apiFavorite.notSearchable ?? false,
      partnerApplicationProduct: apiFavorite.partnerApplicationProduct ?? false,
      partnerProduct: apiFavorite.partnerProduct ?? false,
      prices: apiFavorite.prices,
      purchasePossible: apiFavorite.purchasePossible ?? false,
      selectedAmount: apiFavorite.selectedAmount,
      setContents: apiFavorite.setContents ?? {},
      showOnFrontpage: apiFavorite.showOnFrontpage ?? false,
      singleShipmentOnly: apiFavorite.singleShipmentOnly ?? false,
      skinTypes: apiFavorite.skinTypes ?? null,
      soldOut: apiFavorite.soldOut ?? false,
      title: apiFavorite.title ?? ``,
      unit: `products-ufe:productInfos.units.unit${apiFavorite.unitId}` ?? ``,
      unitContents: `products-ufe:productInfos.units.unit${apiFavorite.unitContentsId}` ?? ``,
      unitContentsId: apiFavorite.unitContentsId ?? 0,
      unitId: apiFavorite.unitId ?? 0,
      usp: apiFavorite.usp ?? ``,
    };
  }

  public static transformApiCustomerDataSheetAddressesToCustomerDataSheetAddresses(apiAddresses: { [index: string]: IApiCustomerDataSheetAddress }): ICustomerDataSheetAddress[] {
    return Object.values(apiAddresses).map(apiAddress => TransformHelper.transformApiCustomerDataSheetAddressToCustomerDataSheetAddress(apiAddress));
  }

  public static transformApiCustomerDataSheetAddressToCustomerDataSheetAddress(apiAddress: IApiCustomerDataSheetAddress): ICustomerDataSheetAddress {
    return {
      city: apiAddress.city ?? ``,
      country: apiAddress.country,
      countryIso: apiAddress.countryIso,
      defaultDeliveryAddress: apiAddress.defaultDeliveryAddress ?? false,
      extra: apiAddress.extra ?? ``,
      invoiceAddress: apiAddress.invoiceAddress ?? false,
      number: apiAddress.number ?? 0,
      recipient: apiAddress.recipient ?? ``,
      street: apiAddress.street ?? ``,
      zipcode: apiAddress.zipcode ?? ``,
    };
  }

  public static transformApiCustomerDataSheetContactsToCustomerDataSheetContacts(apiContacts: { [index: string]: IApiCustomerDataSheetContact }): ICustomerDataSheetContact[] {
    return Object.values(apiContacts).map(apiContact => TransformHelper.transformApiCustomerDataSheetContactToCustomerDataSheetContact(apiContact));
  }

  public static transformApiCustomerDataSheetContactToCustomerDataSheetContact(apiContact: IApiCustomerDataSheetContact): ICustomerDataSheetContact {
    return {
      countryIso: apiContact.landIso,
      phoneAreaCode: apiContact.phoneAreaCode ?? ``,
      phoneCountryCode: apiContact.phoneCountryCode ?? ``,
      phoneNumber: apiContact.phoneNumber ?? ``,
      type: apiContact.type ?? 0,
    };
  }

  public static transformApiCustomerDataSheetInvoicesToCustomerDataSheetInvoices(apiInvoices: { [index: string]: IApiCustomerDataSheetInvoice }): ICustomerDataSheetInvoice[] {
    return Object.values(apiInvoices).map(apiInvoice => TransformHelper.transformApiCustomerDataSheetInvoiceToCustomerDataSheetInvoice(apiInvoice));
  }

  public static transformApiCustomerDataSheetInvoiceToCustomerDataSheetInvoice(apiInvoice: IApiCustomerDataSheetInvoice): ICustomerDataSheetInvoice {
    return {
      currency: apiInvoice.currency,
      date: apiInvoice.date,
      invoiceId: apiInvoice.invoiceId ?? 0,
      invoiceNumber: apiInvoice.invoiceNumber ?? ``,
      paymentType: apiInvoice.paymentType,
      total: apiInvoice.total ?? 0,
    };
  }

  public static transformApiCouponsToCoupons(apiCoupons: { [index: string]: IApiCoupon }): ICoupon[] {
    return Object.values(apiCoupons).map(apiCoupon => TransformHelper.transformApiCouponToCoupon(apiCoupon));
  }

  public static transformApiCouponToCoupon(apiCoupon: IApiCoupon): ICoupon {
    return {
      availableValue: apiCoupon.availableValue ?? 0,
      code: apiCoupon.code ?? ``,
      currency: apiCoupon.currency?.toUpperCase() as ECurrency ?? ECurrency.EUR,
      daysRemaining: apiCoupon.expiryDate ? differenceInDays(new Date(apiCoupon.expiryDate), new Date()).toString() : ``,
      expiryDate: apiCoupon.expiryDate ?? ``,
      minimumOrderValue: apiCoupon.minimumOrderValue ?? 0,
      texts: apiCoupon.texts ?? [``],
    };
  }

  public static transformApiCouponsToCustomerCoupons(apiCoupons: IApiCoupon[]): ICustomerCoupon[] {
    return apiCoupons.map(apiCoupon => TransformHelper.transformApiCouponToCustomerCoupon(apiCoupon));
  }

  public static transformApiCouponToCustomerCoupon(apiCoupon: IApiCoupon): ICustomerCoupon {
    return {
      autoRedeem: apiCoupon.autoRedeem,
      availableValue: apiCoupon.availableValue,
      code: apiCoupon.code,
      currency: apiCoupon.currency as ECurrency,
      expiryDate: apiCoupon.expiryDate,
      generic: apiCoupon.generic,
      matchcode: apiCoupon.matchcode,
      minimumOrderValue: apiCoupon.minimumOrderValue,
      texts: apiCoupon.texts,
    };
  }

  public static transformApiDatasheetOrdersToDatasheetOrders(apiOrders: IApiDataSheetOrder[]): IDataSheetOrder[] {
    return apiOrders.map(apiOrder => TransformHelper.transformApiDatasheetOrderToDatasheetOrder(apiOrder));
  }

  public static transformApiDatasheetOrderToDatasheetOrder(apiOrder: IApiDataSheetOrder): IDataSheetOrder {
    return {
      date: apiOrder.date ?? ``,
      downloadUrl: apiOrder.downloadUrl ?? ``,
      invoiceId: apiOrder.invoiceId ?? 0,
      invoiceNumber: apiOrder.invoiceNumber ?? ``,
      orderNumber: apiOrder.orderNumber ?? ``,
      points: apiOrder.points ?? -1,
      status: apiOrder.status ?? ``,
    };
  }

  public static transformDatasheetOrdersFilterWithCNToApiDatasheetOrdersFilter(filter: IDatasheetOrdersFilter & { customerNumber: number }): IApiDatasheetOrdersFilter {
    return {
      customerNumber: filter.customerNumber,
      dateFrom: filter.from !== `` ? `${format(parse(filter.from, `dd.MM.yyyy`, new Date()), `yyyy-MM-dd`)}T00:00:00` : undefined,
      dateTo: filter.to !== `` ? `${format(parse(filter.to, `dd.MM.yyyy`, new Date()), `yyyy-MM-dd`)}T23:59:59` : undefined,
      status: filter.status !== `` ? filter.status : undefined,
    };
  }

  public static transformApiAssignableVoucherInfosToAssignableVoucherInfos(apiVoucherInfos: IApiAssignableVoucherInfo[]): IAssignableVoucherInfo[] {
    return apiVoucherInfos.map(apiVoucherInfo => TransformHelper.transformApiAssignableVoucherInfoToAssignableVoucherInfo(apiVoucherInfo));
  }

  public static transformApiAssignableVoucherInfoToAssignableVoucherInfo(apiVoucherInfo: IApiAssignableVoucherInfo): IAssignableVoucherInfo {
    return {
      customerName: apiVoucherInfo.customerName,
      customerNumber: apiVoucherInfo.customerNumber,
      personalMessage: apiVoucherInfo.personalMessage,
      value: apiVoucherInfo.amount,
      voucherCode: apiVoucherInfo.voucherCode,
      voucherType: (Object.values(EVoucherType) as string[]).includes(apiVoucherInfo.voucherType?.toLowerCase())
        ? apiVoucherInfo.voucherType.toLowerCase() as EVoucherType
        : undefined,
    };
  }

  public static transformShoppingCartItemsToTrackingProductData(shoppingCartItems: IShoppingCartItem[]): ITrackingProductData[] {
    return shoppingCartItems.map(shoppingCartItem => TransformHelper.transformShoppingCartItemToTrackingProductData(shoppingCartItem));
  }

  public static transformShoppingCartItemToTrackingProductData(shoppingCartItem: IShoppingCartItem): ITrackingProductData {
    return {
      amount: shoppingCartItem.amount,
      category: ``,
      matchcode: shoppingCartItem.matchcode,
      price: parseFloat(shoppingCartItem.price.normal.toFixed(2)),
      title: shoppingCartItem.title ?? ``,
    };
  }

  public static transformTrackingProductDataToPinterestProductData(trackingProductData: ITrackingProductData): IPinterestProductData {
    return {
      product_category: trackingProductData.category,
      product_id: trackingProductData.matchcode,
      product_name: trackingProductData.title,
      product_price: trackingProductData.price,
      product_quantity: trackingProductData.amount,
    };
  }
}
